import { useEffect, useState, type FormEvent } from 'react'
import { browserSupportsWebAuthn, browserSupportsWebAuthnAutofill, startAuthentication } from '@simplewebauthn/browser'
import { LoopApiError } from '@innerloopco/loopcms-core'
import { Button, Field, Input } from '../../components/index.js'
import { api } from '../../lib/api.js'
import { useAuth } from '../../lib/auth.js'
import { SignedOutLayout } from './SignedOutLayout.js'

/** A six-digit authenticator code or one of the recovery codes shown at enrolment. */
const RECOVERY_CODE_PATTERN = /^[a-z2-7]{5}-[a-z2-7]{5}$/
const isAcceptableCode = (code: string): boolean => /^\d{6}$/.test(code) || RECOVERY_CODE_PATTERN.test(code)

const FORGOT_SENT =
  'If that address has an account, a reset link is on its way.'

export function SignIn() {
  const { status, methods, signIn, verifyTotp, signInWithPasskey, adopt, restart } = useAuth()
  // The instance is the server this page came from — nothing to type.
  const instance = window.location.host
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [forgot, setForgot] = useState(false)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotSent, setForgotSent] = useState(false)
  const passkeys = browserSupportsWebAuthn()

  // The browser offers saved passkeys right in the email field (conditional
  // UI) while this screen is up; picking one signs straight in.
  useEffect(() => {
    if (status !== 'signed-out' || forgot) return
    let cancelled = false
    void (async () => {
      if (!(await browserSupportsWebAuthnAutofill())) return
      try {
        const optionsJSON = await api.passkeySignInOptions()
        if (cancelled) return
        const response = await startAuthentication({ optionsJSON: optionsJSON as never, useBrowserAutofill: true })
        const result = await api.passkeySignIn(response)
        if (!cancelled && result.user) adopt(result.user)
      } catch {
        // Dismissed, superseded by the button, or no passkey: the form still works.
      }
    })()
    return () => { cancelled = true }
  }, [status, forgot, adopt])

  const run = async (action: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (caught) {
      setError(caught instanceof LoopApiError ? caught.message : 'Something went wrong. Try again.')
    } finally {
      setBusy(false)
    }
  }

  const onCredentials = (event: FormEvent) => {
    event.preventDefault()
    // Client-side guard with the design's exact copy. Saves a round trip and,
    // more importantly, is the same sentence the server would return.
    if (password.length < 4) {
      setError('Enter your password to continue.')
      return
    }
    void run(() => signIn(instance, email, password))
  }

  const onCode = (event: FormEvent) => {
    event.preventDefault()
    if (!isAcceptableCode(code)) {
      setError('That code needs six digits.')
      return
    }
    void run(() => verifyTotp(code))
  }

  const onForgot = (event: FormEvent) => {
    event.preventDefault()
    void run(async () => {
      await api.forgotPassword(forgotEmail)
      setForgotSent(true)
    })
  }

  const openForgot = () => {
    setForgotEmail(email)
    setForgotSent(false)
    setError(null)
    setForgot(true)
  }

  const awaitingTotp = status === 'awaiting-totp'
  const title = awaitingTotp ? 'Confirm it’s you' : forgot ? 'Reset your password' : 'Sign in to LoopCMS'

  return (
    <SignedOutLayout
      title={title}
      subtitle={
        awaitingTotp || forgot ? undefined : (
          <p className="text-muted" style={{ margin: 0, fontSize: 13 }} data-testid="instance-host">
            Signing in to <span className="lc-host-chip">{instance}</span>
          </p>
        )
      }
    >
      {awaitingTotp ? (
        <form className="stack" style={{ gap: 'var(--space-4)' }} onSubmit={onCode}>
          <p className="text-muted" style={{ fontSize: 13, margin: 0 }}>
            {methods?.totp && methods.passkey
              ? 'Enter the code from your authenticator app, or use your passkey.'
              : methods?.passkey
                ? 'Confirm it’s you with your passkey.'
                : 'Enter the code from your authenticator app.'}
          </p>

          {methods?.passkey ? (
            <Button variant={methods.totp ? 'secondary' : 'primary'} block disabled={busy} onClick={() => void run(() => signInWithPasskey(true))} style={{ minHeight: 42 }}>
              Use your passkey
            </Button>
          ) : null}

          <Field
            label={methods?.totp === false ? 'Recovery code' : 'Six-digit code'}
            help={methods?.totp === false ? 'Lost your passkey? One of the recovery codes you saved.' : 'Six digits from your authenticator app, or one of your recovery codes.'}
            error={error ?? undefined}
          >
            <Input
              value={code}
              onChange={(event) => setCode(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 11))}
              inputMode="text"
              autoComplete="one-time-code"
              autoFocus={methods?.totp !== false}
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                fontSize: 17,
                letterSpacing: '0.22em',
                minHeight: 44,
              }}
            />
          </Field>

          <Button type="submit" variant={methods?.totp === false ? 'secondary' : 'primary'} block disabled={busy} style={{ minHeight: 42 }}>
            {busy ? 'Verifying…' : methods?.totp === false ? 'Use recovery code' : 'Verify and sign in'}
          </Button>
          <Button variant="ghost" onClick={restart}>Use a different account</Button>
        </form>
      ) : forgot ? (
        <form className="stack" style={{ gap: 'var(--space-4)' }} onSubmit={onForgot}>
          <Field label="Email" error={error ?? undefined}>
            <Input
              type="email"
              value={forgotEmail}
              onChange={(event) => setForgotEmail(event.target.value)}
              autoComplete="username"
              autoFocus
              required
            />
          </Field>

          {forgotSent ? (
            <p role="status" className="text-muted" style={{ margin: 0, fontSize: 12 }}>{FORGOT_SENT}</p>
          ) : null}

          <Button type="submit" variant="primary" block disabled={busy} style={{ minHeight: 42 }}>
            {busy ? 'Sending…' : 'Send reset link'}
          </Button>
          <Button variant="ghost" onClick={() => { setForgot(false); setError(null) }}>Back to sign in</Button>
        </form>
      ) : (
        <form className="stack" style={{ gap: 'var(--space-4)' }} onSubmit={onCredentials}>
          <Field label="Email">
            <Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username webauthn" />
          </Field>
          <Field label="Password" error={error ?? undefined}>
            <Input type="password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" />
          </Field>
          <a
            href="#forgot"
            onClick={(event) => {
              event.preventDefault()
              openForgot()
            }}
            style={{ alignSelf: 'flex-end', marginTop: 'calc(-1 * var(--space-2))', fontSize: 12.5, textDecoration: 'none' }}
          >
            Forgot your password?
          </a>

          <Button type="submit" variant="primary" block disabled={busy} style={{ minHeight: 42 }}>
            {busy ? 'Signing in…' : 'Continue'}
          </Button>
          {passkeys ? (
            <Button variant="secondary" block disabled={busy} onClick={() => void run(() => signInWithPasskey(false))}>
              Sign in with a passkey
            </Button>
          ) : null}
        </form>
      )}
    </SignedOutLayout>
  )
}
